import { expose } from 'comlink';
import { IdbSystem2d } from '../../asset/asset.dto';
import { collection, DocumentSnapshot, getDocs, getFirestore, limit, orderBy, Query, query, startAfter } from 'firebase/firestore';
import { AbstractWarehouseDataSyncStepWorker } from './abstract-warehouse-worker.utils';
import { WarehouseSyncStepTaskContext } from '../executors';
import type { ProgressCallback } from '../executors';
import { merge } from 'lodash-es';
import { QueryConstraint } from '@angular/fire/firestore';
import { SYSTEM2DS_STORE, SYSTEMS_COLLECTION, WW_WAREHOUSES_COLLECTION, logger } from '@damofire/data-commons';

const DOWNLOAD_LIMIT = 1000;

export class WarehouseSystemsStepsWorker extends AbstractWarehouseDataSyncStepWorker<IdbSystem2d> {
  constructor() {
    super(SYSTEM2DS_STORE, 'SYSTEMS');
  }

  protected async clean(ctx: WarehouseSyncStepTaskContext, onProgress?: (progress: number) => void): Promise<void> {
    logger.debug('WarehouseSystemsStepsWorker::clean', { division: ctx.division, category: 'sync' });
    return this.deleteAllDataObsoleteFromStore<IdbSystem2d>(this.store, ctx, (dto) => dto.division._id, undefined, onProgress);
  }

  protected async resolveRemoteData(ctx: WarehouseSyncStepTaskContext, lastSnapshot: DocumentSnapshot, allFromIndex: IdbSystem2d[]): Promise<[DocumentSnapshot, number]> {
    const queryConstraints: QueryConstraint[] = [limit(DOWNLOAD_LIMIT)];

    if (lastSnapshot) {
      queryConstraints.splice(0, 0, startAfter(lastSnapshot));
    }

    const q: Query = query(collection(getFirestore(), `${WW_WAREHOUSES_COLLECTION}/${ctx.division}/${SYSTEMS_COLLECTION}`), orderBy('number'), ...queryConstraints);
    const queryResult = await getDocs(q);

    if (queryResult.docs.length && !this.shouldStopTask) {
      const systems = [];
      for (const i of queryResult.docs) {
        let matching: IdbSystem2d & { syncId?: string } = allFromIndex.find((idbDoc) => idbDoc._id === i.id);
        if (matching) {
          merge(matching, { ...i.data(), syncId: ctx.syncId });
        } else {
          matching = { ...i.data(), division: { _id: ctx.division }, _id: i.id, syncId: ctx.syncId };
        }

        systems.push(matching);
      }

      if (!this.shouldStopTask) {
        logger.info('Downloaded systems from Firestore', { count: systems.length, category: 'sync' });
        await this.idbService.updateAll(this.store, systems).catch(console.error);
      }

      return [queryResult.docs.length === DOWNLOAD_LIMIT ? queryResult.docs[queryResult.docs.length - 1] : null, queryResult.docs.length];
    }

    return [null, 0];
  }

  protected updateTaskProgress(warehouse: string, downloaded: number, onProgress: ProgressCallback): Promise<void> {
    onProgress(0, { processed: downloaded });
    return Promise.resolve(undefined);
  }
}

expose(WarehouseSystemsStepsWorker);
